import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { useGlobal } from '~/hooks/useGlobal';
import { userStore } from '~/stores';
import { CURRENCY_SYMBOL } from '~/stores/constants';

export const formatPrice = (price: number | string): string => {
  const num = Number(price);

  if (isNaN(num) || num === 0) {
    return '0';
  }

  const hasFraction = !Number.isInteger(num);

  const minimumFractionDigits = hasFraction ? 2 : 0;
  const maximumFractionDigits = hasFraction ? 2 : 0;

  return new Intl.NumberFormat('en', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
};

/**
 * Price formatted entirely from the company config rather than a per-tenant
 * hardcode: ISO code from `currencyCode`, locale pinned to the company `lang`
 * (so SSR and the client agree whatever the request's Accept-Language says),
 * whole units only, and the ISO code swapped for the config's own `currency`
 * symbol where the locale placed it — `ru` + UZS + `сум` → `34 990 сум`.
 *
 * A `currency` argument other than the company's (an order paid in another
 * currency) is formatted with its ISO code instead of borrowing the symbol.
 */
const formatWithConfigCurrency = (
  price: string | number,
  currency?: string,
): string => {
  const { lang, currencyCode, currency: symbol } = company.config;
  const code = (currency || currencyCode).toUpperCase();

  const formatted = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: code,
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(price) || 0);

  return code === currencyCode.toUpperCase() && symbol
    ? formatted.replace(code, symbol)
    : formatted;
};

export const notSSRFormatPriceWithCurrency = (
  price: string | number = 0,
  lang?: string,
  currency?: string,
) => {
  const formattedPrice = formatPrice(price);

  const languageToUse = lang || userStore.lang;

  switch (company.name) {
    case CompanyName.Localee: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.CircleK: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? CURRENCY_SYMBOL.egp,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.Kids: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? CURRENCY_SYMBOL.egp,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.Vilo: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? CURRENCY_SYMBOL.idr,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
        .format(Number(price) ?? 0)
        .replaceAll('IDR', 'Rp.');
    }
    case CompanyName.Jiffy: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.Oskuhus: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.Cerise: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'xaf',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.Heimkaup: {
      return `${new Intl.NumberFormat(languageToUse, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'auto',
      })
        .format(Number(price) ?? 0)
        .replace(/,/g, '.')} kr.`;
    }
    case CompanyName.DubaiExpress: {
      return `${new Intl.NumberFormat(languageToUse, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'auto',
      })
        .format(Number(price) ?? 0)
        .replace(/,/g, '.')} ₽`;
    }
    case CompanyName.Storrd: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    case CompanyName.MCosmetic: {
      const currentCurrency = currency ?? 'UZS';
      return new Intl.NumberFormat('ru', {
        style: 'currency',
        currency: currentCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(Number(price) ?? 0)
        .replace(currentCurrency, languageToUse === 'ru' ? 'сум' : 'so’m');
    }
    case CompanyName.Ocado: {
      return formatWithConfigCurrency(price, currency);
    }
    case CompanyName.Electronium: {
      return new Intl.NumberFormat(languageToUse, {
        style: 'currency',
        currency: currency ?? 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(price) ?? 0);
    }
    default:
      return `${CURRENCY_SYMBOL.aed} ${formattedPrice}`;
  }
};

export const formatPriceWithCurrency = (
  price: string | number = 0,
  locale?: string,
  currency?: string,
) => {
  const { serverLang } = useGlobal();
  const languageToUse = import.meta.env.SSR
    ? serverLang
    : (locale ?? userStore.lang);

  return notSSRFormatPriceWithCurrency(price, languageToUse, currency);
};

/** The formatted amount: digits plus any separators standing between them. */
const FORMATTED_AMOUNT = /\d[\d.,\s  ]*\d|\d/;

/**
 * Currency symbol of an already formatted price, e.g. `65 kr.` → `kr.`, for use
 * next to a text label. Read back from a formatted price because each company
 * builds its own symbol (`company.config.currency` is empty for several).
 */
export const extractCurrencySymbol = (formattedPrice: string): string => {
  return formattedPrice.replace(FORMATTED_AMOUNT, '').trim();
};

/** A sign standing immediately before the amount, which belongs to the amount. */
const AMOUNT_SIGN = /[-\u2212+]$/;

/**
 * An already formatted price taken apart into the amount and whatever currency
 * symbol stands on either side of it, so the symbol can be marked up
 * separately. `65 kr.` → `{ prefix: '', amount: '65', suffix: ' kr.' }`.
 *
 * A leading sign stays with the amount — cart rows render discounts as
 * `-${price}`, and a minus parked in the symbol would be dropped by consumers
 * that treat the symbol as decoration.
 */
export const splitFormattedPrice = (
  formattedPrice: string,
): { prefix: string; amount: string; suffix: string } => {
  const match = FORMATTED_AMOUNT.exec(formattedPrice);

  if (!match) {
    return { prefix: formattedPrice, amount: '', suffix: '' };
  }

  const head = formattedPrice.slice(0, match.index);
  const sign = AMOUNT_SIGN.exec(head)?.[0] ?? '';

  return {
    prefix: head.slice(0, head.length - sign.length),
    amount: sign + match[0],
    suffix: formattedPrice.slice(match.index + match[0].length),
  };
};

export const formatNumberWithSpaces = (value: number | string): string =>
  new Intl.NumberFormat('ru', { maximumFractionDigits: 0 }).format(
    Number(value) || 0,
  );

export const toFloat = (number: number | string): number => {
  return parseFloat(number?.toString().replace(',', '.')) || 0;
};

export const toNumber = (input: unknown): number => {
  const num = parseInt(input as string, 10);
  return isNaN(num) ? 0 : num;
};

export const isZero = (number: number | string): boolean => {
  return !toFloat(number);
};

const digitsAfterComma = 2;

export const addZeroFraction = (
  number: number | string,
  isTrimTrailingZeros = true,
): string => {
  number = number.toString().replace(',', '.');
  const splitNumber = number.split('.');
  if (!splitNumber[1]) {
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    return isTrimTrailingZeros
      ? splitNumber[0]
      : `${splitNumber[0]}.${''.padEnd(digitsAfterComma, '0')}`;
  }
  if (isTrimTrailingZeros) {
    return parseFloat(number).toString();
  } else {
    return `${splitNumber[0]}.${splitNumber[1].padEnd(digitsAfterComma, '0')}`;
  }
};

export const convertPoundsToPence = (number: number | string): NumberInt => {
  if (number === undefined) {
    return number;
  }
  if (isZero(number)) {
    return 0;
  }
  return parseInt(addZeroFraction(number, false).replace('.', ''), 10);
};

export const convertPenceToPounds = (number: NumberInt | string): string => {
  if (number === undefined) {
    return number;
  }
  if (isZero(number)) {
    return addZeroFraction('0', company.isTrimTrailingZeros);
  }
  number = number.toString();
  const isNegative = number[0] === '-';
  if (isNegative) {
    number = number.slice(1);
  }
  number = number.padStart(digitsAfterComma + 1, '0');
  const letters = number.split('');
  letters.splice(digitsAfterComma * -1, 0, '.');
  return isNegative
    ? '-' + addZeroFraction(letters.join(''), company.isTrimTrailingZeros)
    : addZeroFraction(letters.join(''), company.isTrimTrailingZeros);
};

export const calcDiscountPercentage = (
  price: NumberInt,
  discountPrice: NumberInt,
): NumberInt => {
  if (isZero(price) || isZero(discountPrice)) {
    return 0;
  }
  const discount = 100 - Math.ceil((discountPrice * 100) / price);
  return discount > 0 ? discount : 0;
};

export const formatRating = (num: number): number => {
  if (Math.floor(num) === num) {
    return num;
  } else {
    return +num.toFixed(1);
  }
};

/**
 * Formats a raw phone source (config value or wa.me link) into a grouped
 * display string, e.g. "+241 77575280" or "https://wa.me/24177400708" →
 * "+241 77 400 708".
 */
export const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
};
